import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CurrencyInput } from '../components/CurrencyInput';

const Settings = () => {
	const [formData, setFormData] = useState({
		eurRate: '',
		usdRate: '',
		usdInterest: '',
		eurInterest: '',
		trlInterest: '',
	});
	const location = useLocation();
	const [loading, setLoading] = useState(false); // Track loading state
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');

	const [islands, setIslands] = useState([]);
	const [selectedIsland, setSelectedIsland] = useState('');
	const [areas, setAreas] = useState([]);
	const [selectedArea, setSelectedArea] = useState('');
	const [blocks, setBlocks] = useState([]);
	const [selectedBlock, setSelectedBlock] = useState('');
	const [blockPrice, setBlockPrice] = useState('');
	const [exchangeRates, setExchangeRates] = useState([]);
	const [initialPrice, setInitialPrice] = useState(null);
	const [minDownPaymentPercent, setMinDownPaymentPercent] = useState('');
	const [maxValidityDays, setMaxValidityDays] = useState('');
	const [note, setNote] = useState('');
	
	// Settings access management
	const [settingsAccessEmail, setSettingsAccessEmail] = useState('');
	const [settingsUsers, setSettingsUsers] = useState([]);
	const [loadingSettingsAccess, setLoadingSettingsAccess] = useState(false);
	const [deleteEmail, setDeleteEmail] = useState('');

	const fetchIslands = async () => {
		try {
			const res = await fetch('/api/islands');
			const data = await res.json();
			setIslands(data);
		} catch (error) {
			console.error('Error fetching islands:', error);
		}
	};

	useEffect(() => {
		fetchIslands();
	}, []);

	// Update areas when an island is selected
	useEffect(() => {
		if (selectedIsland) {
			const island = islands.find((i) => i.id === selectedIsland);
			if (island) {
				const areaList = island.areas.map((area) => area.areaId);
				setAreas(areaList);
				setBlocks([]); // Reset blocks when island changes
				setBlockPrice(''); // Reset price when island changes
			}
		} else {
			setAreas([]);
			setBlocks([]);
			setBlockPrice('');
		}
	}, [selectedIsland, islands]);

	useEffect(() => {
		if (selectedIsland && selectedArea) {
			const island = islands.find((i) => i.id === selectedIsland);
			if (island) {
				const area = island.areas.find(
					(a) => a.areaId === Number(selectedArea)
				);
				if (area) {
					setBlocks(area.buildings);
				}
			}
		} else {
			setBlocks([]);
			setBlockPrice('');
		}
	}, [selectedArea, selectedIsland, islands]);

	// Update price when a block is selected
	useEffect(() => {
		if (selectedIsland && selectedArea && selectedBlock) {
			const island = islands.find((i) => i.id === selectedIsland);
			if (island) {
				const area = island.areas.find(
					(a) => a.areaId === Number(selectedArea)
				);
				if (area) {
					const block = area.buildings.find((b) => b.name === selectedBlock);
					if (block) {
						setBlockPrice(block.value); // Set the price from the block
					}
				}
			}
		} else {
			setBlockPrice('');
		}
	}, [selectedBlock, selectedArea, selectedIsland, islands]);

	const fetchData = async () => {
		setLoading(true);
		try {
			// Exchange rates
			const response = await fetch(
				`/api/exchange-rate?timestamp=${Date.now()}`,
				{
					cache: 'no-store',
				}
			);
			const data = await response.json();
			setFormData({
				eurRate: data.find((item) => item.currency === 'EUR')?.rate || '',
				usdRate: data.find((item) => item.currency === 'USD')?.rate || '',
				usdInterest:
					data.find((item) => item.currency === 'USD')?.interest || '',
				eurInterest:
					data.find((item) => item.currency === 'EUR')?.interest || '',
				trlInterest:
					data.find((item) => item.currency === 'TRY')?.interest || '',
			});

			// App settings
			const settingsRes = await fetch(`/api/app-settings?timestamp=${Date.now()}`, {
				cache: 'no-store',
			});
			const settingsJson = await settingsRes.json();
			setMinDownPaymentPercent(
				settingsJson?.minDownPaymentPercent?.toString?.() ?? ''
			);
			setMaxValidityDays(
				settingsJson?.maxValidityDays?.toString?.() ?? ''
			);
			setNote(settingsJson?.note ?? '');
		} catch (error) {
			console.error('Veriler alınırken hata oluştu:', error);
		} finally {
			setLoading(false);
		}
	};

	// Fetch users with settings access
	const fetchSettingsUsers = async () => {
		try {
			const user = JSON.parse(localStorage.getItem('user'));
			if (!user || !user.token) return;

			const response = await fetch('/api/user/settings-users', {
				headers: {
					'Authorization': `Bearer ${user.token}`,
				},
			});

			if (response.ok) {
				const data = await response.json();
				setSettingsUsers(data);
			}
		} catch (error) {
			console.error('Error fetching settings users:', error);
		}
	};

	useEffect(() => {
		fetchData();
		fetchSettingsUsers();
	}, [location.pathname]);

	// Grant settings access
	const handleGrantSettingsAccess = async (e) => {
		e.preventDefault();
		setLoadingSettingsAccess(true);

		try {
			const user = JSON.parse(localStorage.getItem('user'));
			if (!user || !user.token) {
				alert('Oturum açmanız gerekiyor.');
				return;
			}

			const response = await fetch('/api/user/grant-settings-access', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${user.token}`,
				},
				body: JSON.stringify({ email: settingsAccessEmail }),
			});

			const json = await response.json();

			if (response.ok) {
				alert(json.message || 'Ayarlar sayfasına erişim başarıyla verildi.');
				setSettingsAccessEmail('');
				await fetchSettingsUsers();
			} else {
				alert(json.error || 'Bir hata oluştu.');
			}
		} catch (error) {
			console.error('Error granting settings access:', error);
			alert('Bir hata oluştu, tekrar deneyin.');
		} finally {
			setLoadingSettingsAccess(false);
		}
	};

	// Revoke settings access
	const handleRevokeSettingsAccess = async (email) => {
		if (!window.confirm(`${email} kullanıcısının ayarlar sayfasına erişimini kaldırmak istediğinizden emin misiniz?`)) {
			return;
		}

		try {
			const user = JSON.parse(localStorage.getItem('user'));
			if (!user || !user.token) {
				alert('Oturum açmanız gerekiyor.');
				return;
			}

			const response = await fetch('/api/user/revoke-settings-access', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${user.token}`,
				},
				body: JSON.stringify({ email }),
			});

			const json = await response.json();

			if (response.ok) {
				alert(json.message || 'Ayarlar sayfasına erişim başarıyla kaldırıldı.');
				await fetchSettingsUsers();
			} else {
				alert(json.error || 'Bir hata oluştu.');
			}
		} catch (error) {
			console.error('Error revoking settings access:', error);
			alert('Bir hata oluştu, tekrar deneyin.');
		}
	};

	// Handle input changes
	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleSubmitBlock = async (e) => {
		e.preventDefault();

		const data = {
			islandNo: selectedIsland, // E.g., "111"
			areaId: selectedArea, // E.g., 1
			buildingName: selectedBlock, // E.g., "B1"
			price: blockPrice,
		};

		console.log('Request data:', data);

		try {
			const response = await fetch('/api/islands/update', {
				method: 'PATCH',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(data),
			});

			const json = await response.json();
			console.log('Response:', json);

			if (response.ok) {
				alert('Bina fiyatı başarıyla güncellendi!');
				await fetchIslands();
			} else {
				alert(json.message || 'Bir hata oluştu.');
			}
		} catch (error) {
			console.error('Güncelleme hatası:', error);
			alert('Bir hata oluştu, tekrar deneyin.');
		}
	};

	const selectedBlockSold = blocks.find((b) => b.name === selectedBlock)?.sold;

	const handleToggleSold = async () => {
		const sold = !selectedBlockSold;
		const confirmMessage = sold
			? `${selectedBlock} bloğunu satıldı olarak işaretlemek istediğinize emin misiniz? Yeni tekliflerde görünmeyecek.`
			: `${selectedBlock} bloğunu tekrar satışa açmak istediğinize emin misiniz?`;
		if (!window.confirm(confirmMessage)) return;

		try {
			const response = await fetch('/api/islands/sold', {
				method: 'PATCH',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					islandNo: selectedIsland,
					areaId: selectedArea,
					buildingName: selectedBlock,
					sold,
				}),
			});

			const json = await response.json();

			if (response.ok) {
				alert(json.message);
				await fetchIslands();
			} else {
				alert(json.message || 'Bir hata oluştu.');
			}
		} catch (error) {
			console.error('Satış durumu güncelleme hatası:', error);
			alert('Bir hata oluştu, tekrar deneyin.');
		}
	};

	// Send PATCH request
	const handleSubmit = async (e) => {
		e.preventDefault();

		const updates = {
			USD: {},
			EUR: {},
			TRY: {},
		};

		// Check for changes and assign to correct currency
		if (formData.usdRate) updates.USD.rate = parseFloat(formData.usdRate);
		if (formData.eurRate) updates.EUR.rate = parseFloat(formData.eurRate);
		if (formData.usdInterest)
			updates.USD.interest = parseFloat(formData.usdInterest);
		if (formData.eurInterest)
			updates.EUR.interest = parseFloat(formData.eurInterest);
		if (formData.trlInterest)
			updates.TRY.interest = parseFloat(formData.trlInterest);

		console.log(updates);

		// Perform PATCH requests for each currency that has an update
		try {
			const patchRequests = Object.entries(updates)
				.filter(([_, data]) => Object.keys(data).length > 0) // Only update if there's a change
				.map(([currency, data]) =>
					fetch(`/api/exchange-rate/${currency}`, {
						method: 'PATCH',
						headers: { 'Content-Type': 'application/json' },
						body: JSON.stringify(data),
					})
				);

			// Execute all PATCH requests in parallel
			await Promise.all(patchRequests);

			// Update app settings
			const appSettingsUpdates = {};
			if (minDownPaymentPercent !== '') {
				appSettingsUpdates.minDownPaymentPercent = parseFloat(minDownPaymentPercent);
			}
			if (maxValidityDays !== '') {
				appSettingsUpdates.maxValidityDays = parseInt(maxValidityDays, 10);
			}
			if (note !== undefined) {
				appSettingsUpdates.note = note;
			}
			if (Object.keys(appSettingsUpdates).length > 0) {
				await fetch('/api/app-settings', {
					method: 'PATCH',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify(appSettingsUpdates),
				});
			}

			alert('Değerler başarıyla güncellendi!');
			await fetchData();
		} catch (error) {
			console.error('Güncelleme hatası:', error);
			alert('Bir hata oluştu, tekrar deneyin.');
		}
	};

	const handleSubmitAdd = async (e) => {
		e.preventDefault();

		const response = await fetch('/api/user/signup', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ email, password }),
		});

		const json = await response.json();

		if (response.ok) {
			alert('Kullanıcı başarıyla eklendi.');
			setEmail('');
			setPassword('');
		} else {
			alert(json.error || 'Bir hata oluştu.');
		}
	};

	const handleDeleteUser = async (e) => {
		e.preventDefault();
		if (!deleteEmail) {
			alert('Silmek istediğiniz kullanıcının email adresini girin.');
			return;
		}

		if (
			!window.confirm(
				`${deleteEmail} kullanıcısını tamamen silmek istediğinize emin misiniz?`
			)
		) {
			return;
		}

		try {
			const user = JSON.parse(localStorage.getItem('user'));
			if (!user || !user.token) {
				alert('Oturum açmanız gerekiyor.');
				return;
			}

			const response = await fetch('/api/user/delete', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${user.token}`,
				},
				body: JSON.stringify({ email: deleteEmail }),
			});

			const json = await response.json();

			if (response.ok) {
				alert(json.message || 'Kullanıcı başarıyla silindi.');
				setDeleteEmail('');
				await fetchSettingsUsers();
			} else {
				alert(json.error || 'Bir hata oluştu.');
			}
		} catch (error) {
			console.error('Error deleting user:', error);
			alert('Bir hata oluştu, tekrar deneyin.');
		}
	};

	return (
		<div className="settings-page">
			<div className="settings-header">
				<h2>Ayarlar</h2>
				<p>
					Kurları, teklif kurallarını, blok fiyatlarını ve kullanıcıları buradan
					yönetin.
				</p>
			</div>

			<div className="settings-grid">
				{/* Rates & quote rules */}
				<form
					className="settings-card settings-card--wide"
					onSubmit={handleSubmit}
				>
					<div className="settings-card__header">
						<h3>Kurlar ve Teklif Ayarları</h3>
						{loading && <span className="settings-badge">Yükleniyor...</span>}
					</div>

					<div className="settings-section">
						<h4>Döviz Kurları</h4>
						<div className="settings-row">
							<Field label="Dolar Kuru">
								<input
									name="usdRate"
									inputMode="decimal"
									value={formData.usdRate}
									onChange={handleChange}
								/>
							</Field>
							<Field label="Euro Kuru">
								<input
									name="eurRate"
									inputMode="decimal"
									value={formData.eurRate}
									onChange={handleChange}
								/>
							</Field>
						</div>
					</div>

					<div className="settings-section">
						<h4>Yıllık Faiz Oranları</h4>
						<div className="settings-row">
							<Field label="Dolar" suffix="%">
								<input
									name="usdInterest"
									inputMode="decimal"
									value={formData.usdInterest}
									onChange={handleChange}
								/>
							</Field>
							<Field label="Euro" suffix="%">
								<input
									name="eurInterest"
									inputMode="decimal"
									value={formData.eurInterest}
									onChange={handleChange}
								/>
							</Field>
							<Field label="TL" suffix="%">
								<input
									name="trlInterest"
									inputMode="decimal"
									value={formData.trlInterest}
									onChange={handleChange}
								/>
							</Field>
						</div>
					</div>

					<div className="settings-section">
						<h4>Teklif Kuralları</h4>
						<div className="settings-row">
							<Field label="Asgari Peşinat" suffix="%">
								<input
									type="number"
									min="0"
									max="100"
									step="0.01"
									value={minDownPaymentPercent}
									onChange={(e) => setMinDownPaymentPercent(e.target.value)}
								/>
							</Field>
							<Field label="Maks. Teklif Geçerliliği" suffix="gün">
								<input
									type="number"
									min="1"
									step="1"
									value={maxValidityDays}
									onChange={(e) => setMaxValidityDays(e.target.value)}
									placeholder="Örn: 30"
								/>
							</Field>
						</div>
						<Field
							label="Excel Notu"
							hint="Oluşturulan Excel dosyasının altında görünür."
						>
							<textarea
								value={note}
								onChange={(e) => setNote(e.target.value)}
								placeholder="Excel dosyasının altında görünecek notu buraya yazın"
								rows="4"
							/>
						</Field>
					</div>

					<div className="settings-actions">
						<button type="submit" className="settings-btn" disabled={loading}>
							{loading ? 'Güncelleniyor...' : 'Değişiklikleri Kaydet'}
						</button>
					</div>
				</form>

				{/* Block price */}
				<form
					className="settings-card"
					autoComplete="off"
					onSubmit={handleSubmitBlock}
				>
					<div className="settings-card__header">
						<h3>Blok Fiyatını Güncelle</h3>
					</div>
					<p className="settings-card__desc">
						Ada, parsel ve blok seçin; mevcut fiyat otomatik olarak gelir.
					</p>

					<div className="settings-row settings-row--3">
						<Field label="Ada">
							<select
								value={selectedIsland}
								onChange={(e) => {
									setSelectedIsland(e.target.value);
									setSelectedArea('');
									setSelectedBlock('');
								}}
							>
								<option value="" disabled>
									Seçin
								</option>
								{islands.map((island) => (
									<option key={island.id} value={island.id}>
										{island.id}
									</option>
								))}
							</select>
						</Field>
						<Field label="Parsel">
							<select
								value={selectedArea}
								disabled={!selectedIsland}
								onChange={(e) => {
									setSelectedArea(e.target.value);
									setSelectedBlock('');
								}}
							>
								<option value="" disabled>
									Seçin
								</option>
								{areas.map((area) => (
									<option key={area} value={area}>
										{area}
									</option>
								))}
							</select>
						</Field>
						<Field label="Blok">
							<select
								value={selectedBlock}
								disabled={!selectedArea}
								onChange={(e) => setSelectedBlock(e.target.value)}
							>
								<option value="" disabled>
									Seçin
								</option>
								{blocks.map((block) => (
									<option key={block.name} value={block.name}>
										{block.sold ? `${block.name} (Satıldı)` : block.name}
									</option>
								))}
							</select>
						</Field>
					</div>

					<Field label="Yeni Fiyat (USD)">
						<CurrencyInput
							value={blockPrice}
							currency="USD"
							allowDecimals
							disabled={!selectedBlock}
							onChange={setBlockPrice}
						/>
					</Field>

					<div className="settings-actions">
						<button
							type="button"
							className={
								selectedBlockSold
									? 'settings-btn settings-btn--ghost-danger'
									: 'settings-btn settings-btn--danger'
							}
							disabled={!selectedBlock}
							onClick={handleToggleSold}
						>
							{selectedBlockSold ? 'Tekrar Satışa Aç' : 'Satıldı Olarak İşaretle'}
						</button>
						<button
							type="submit"
							className="settings-btn"
							disabled={!selectedBlock || !blockPrice}
						>
							Fiyatı Güncelle
						</button>
					</div>
				</form>

				{/* Settings access */}
				<div className="settings-card">
					<div className="settings-card__header">
						<h3>Ayarlar Erişimi</h3>
						<span className="settings-badge">
							{settingsUsers.length} kullanıcı
						</span>
					</div>
					<p className="settings-card__desc">
						Bu sayfayı görüntüleyip düzenleyebilecek kullanıcılar.
					</p>

					<form className="settings-inline" onSubmit={handleGrantSettingsAccess}>
						<input
							type="email"
							placeholder="Kullanıcı email adresi"
							onChange={(e) => setSettingsAccessEmail(e.target.value)}
							value={settingsAccessEmail}
							required
						/>
						<button
							type="submit"
							className="settings-btn"
							disabled={loadingSettingsAccess}
						>
							{loadingSettingsAccess ? 'İşleniyor...' : 'Erişim Ver'}
						</button>
					</form>

					{settingsUsers.length === 0 ? (
						<p className="settings-empty">
							Henüz ayarlar sayfasına erişimi olan kullanıcı yok.
						</p>
					) : (
						<ul className="settings-user-list">
							{settingsUsers.map((user) => (
								<li key={user._id}>
									<span className="settings-user-list__email">
										{user.email}
									</span>
									<button
										type="button"
										className="settings-btn settings-btn--ghost-danger"
										onClick={() => handleRevokeSettingsAccess(user.email)}
									>
										Erişimi Kaldır
									</button>
								</li>
							))}
						</ul>
					)}
				</div>

				{/* Users */}
				<div className="settings-card settings-card--wide">
					<div className="settings-card__header">
						<h3>Kullanıcılar</h3>
					</div>

					<div className="settings-split">
						<form className="settings-section" onSubmit={handleSubmitAdd}>
							<h4>Kullanıcı Ekle</h4>
							<Field label="Email">
								<input
									type="email"
									required
									onChange={(e) => setEmail(e.target.value)}
									value={email}
								/>
							</Field>
							<Field
								label="Şifre"
								hint="En az 1 büyük harf, 1 küçük harf, rakam ve sembol içermelidir."
							>
								<input
									type="password"
									required
									onChange={(e) => setPassword(e.target.value)}
									value={password}
								/>
							</Field>
							<div className="settings-actions">
								<button type="submit" className="settings-btn">
									Kullanıcı Ekle
								</button>
							</div>
						</form>

						<form
							className="settings-section settings-danger"
							onSubmit={handleDeleteUser}
						>
							<h4>Kullanıcı Sil</h4>
							<div className="settings-inline">
								<input
									type="email"
									value={deleteEmail}
									onChange={(e) => setDeleteEmail(e.target.value)}
									placeholder="Silinecek email adresi"
								/>
								<button
									type="submit"
									className="settings-btn settings-btn--danger"
								>
									Sil
								</button>
							</div>
						</form>
					</div>
				</div>
			</div>
		</div>
	);
};

const Field = ({ label, hint, suffix, children }) => (
	<label className="settings-field">
		<span className="settings-field__label">{label}</span>
		<span
			className={
				suffix ? 'settings-field__control has-suffix' : 'settings-field__control'
			}
		>
			{children}
			{suffix && <span className="settings-field__suffix">{suffix}</span>}
		</span>
		{hint && <span className="settings-field__hint">{hint}</span>}
	</label>
);

export default Settings;
